import { reduce } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

type AdfDocument = ADFEntity | Record<string, unknown>;
type AdfInput = string | AdfDocument | null | undefined;

const parseAdf = (adf: AdfInput): AdfDocument | null => {
	if (!adf) {
		return null;
	}
	if (typeof adf === 'string') {
		try {
			return JSON.parse(adf) as Record<string, unknown>;
		} catch {
			return null;
		}
	}
	return adf;
};

/**
 * Flattens an ADF document (object or JSON string) to plain text for keyword
 * matching. Text nodes are joined with single spaces so that words in adjacent
 * nodes/blocks keep a boundary between them and are not accidentally fused into
 * a single token. Returns an empty string when the input is missing or invalid.
 *
 * The extracted text is used only for in-memory matching and never leaves the
 * client.
 */
export const adfToPlainText = (adf: AdfInput): string => {
	const doc = parseAdf(adf);
	if (!doc) {
		return '';
	}
	const text = reduce<string>(
		// Best-effort: the parsed value is treated as an ADFEntity for traversal.
		doc as Parameters<typeof reduce>[0],
		(acc, node) => {
			if (node.type === 'text' && typeof node.text === 'string') {
				return `${acc}${node.text} `;
			}
			return acc;
		},
		'',
	);
	return text.replace(/\s+/g, ' ').trim();
};
